import React, { useState } from "react";
import { toast } from "material-react-toastify";
import { portfolioData } from "../data/portfolioData";

const initialForm = {
  name: "",
  email: "",
  message: "",
};

const validateForm = (values) => {
  const errors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!values.name.trim()) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters.";
  }

  if (!values.email.trim()) {
    errors.email = "Email is required.";
  } else if (!emailPattern.test(values.email.trim())) {
    errors.email = "Enter a valid email address.";
  }

  if (!values.message.trim()) {
    errors.message = "Message is required.";
  } else if (values.message.trim().length < 20) {
    errors.message = "Message must be at least 20 characters.";
  }

  return errors;
};

const Contact = () => {
  const { email, linkedin, github, sections, contact } = portfolioData;
  const [formValues, setFormValues] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setIsSubmitting(true);
    window.setTimeout(() => {
      setIsSubmitting(false);
      setFormValues(initialForm);
      toast.success("Message sent successfully. I will get back to you soon.");
    }, 500);
  };

  const inputClass = (field) =>
    `mt-2 w-full rounded-lg border bg-white px-3 py-3 text-sm text-slate-900 outline-none transition ${
      formErrors[field]
        ? "border-rose-400 focus:border-rose-400"
        : "border-slate-300 focus:border-indigo-600"
    }`;

  return (
    <section id="contact" className="scroll-mt-24 py-14">
      <div className="mx-auto grid max-w-6xl gap-6 px-4 md:px-6 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-2xl border border-slate-200 bg-white/80 p-7 shadow-lg shadow-indigo-100/50">
          <h2 className="text-3xl font-bold text-slate-900">{sections.contactTitle}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-700">
            {contact.introPrefix}{" "}
            <a href={`mailto:${email}`} className="font-medium text-indigo-700 hover:text-indigo-600">
              {email}
            </a>{" "}
            {contact.introConnector}{" "}
            <a
              href={linkedin}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-indigo-700 hover:text-indigo-600"
            >
              {contact.linkedinLabel}
            </a>
            {" & "}
            <a
              href={github}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-indigo-700 hover:text-indigo-600"
            >
              {contact.githubLabel}
            </a>
            {contact.introSuffix}
          </p>
        </div>

        <form
          className="grid gap-4 rounded-2xl border border-slate-200 bg-white/80 p-7 text-sm shadow-lg shadow-indigo-100/50"
          onSubmit={onSubmit}
          noValidate
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                {contact.form.nameLabel}
              </label>
              <input
                type="text"
                name="name"
                value={formValues.name}
                onChange={onInputChange}
                className={inputClass("name")}
                placeholder={contact.form.namePlaceholder}
              />
              {formErrors.name && (
                <p className="mt-1 text-xs text-rose-300">{formErrors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                {contact.form.emailLabel}
              </label>
              <input
                type="email"
                name="email"
                value={formValues.email}
                onChange={onInputChange}
                className={inputClass("email")}
                placeholder={contact.form.emailPlaceholder}
              />
              {formErrors.email && (
                <p className="mt-1 text-xs text-rose-300">{formErrors.email}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
              {contact.form.messageLabel}
            </label>
            <textarea
              rows="5"
              name="message"
              value={formValues.message}
              onChange={onInputChange}
              className={inputClass("message")}
              placeholder={contact.form.messagePlaceholder}
            />
            {formErrors.message && (
              <p className="mt-1 text-xs text-rose-300">{formErrors.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-fit rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Sending..." : contact.form.submitLabel}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
